import React, { useEffect, useState } from "react";
import useApi from "../../../hooks/useGaneshApi";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { GridColDef, getGridStringOperators } from "@mui/x-data-grid-premium";
import LeafletMapPlaces from "../../../components/UI/LeafletMapPlaces";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import {
  RiArrowDownSLine,
  RiClipboardLine,
  RiCloseLine,
  RiExternalLinkLine,
} from "@remixicon/react";
import "../BrandMetadataReview.css";

import { Place_Detail_T } from "../../../types/Place";
import { Link } from "react-router-dom";
import ExternalLinksButtonGroup from "../../../components/UI/ExternalLinksButtonGroup";
import PlaceDetailsModalCell from "../UI/PlaceDetailsModallCell";

interface PlacesDrillDownModalProps {
  api: string;
  apiParams: Record<string, any>;
}

const columns_def: GridColDef[] = [
  {
    field: "place_id",
    headerName: "Place ID",
    description: "The unique identifier of the place.",
    type: "string",
    width: 200,
  },
  {
    field: "name",
    headerName: "Place Name",
    description: "The name of the place.",
    type: "string",
    width: 250,
  },
  {
    field: "brand_id",
    headerName: "Brand ID",
    type: "string",
    width: 200,
  },
  {
    field: "external_links",
    headerName: "External Links",
    description: "View the place in Google maps or Kuebiko.",
    width: 200,
    filterable: true,
    type: "string",
    renderCell: (params: any) => (
      <ExternalLinksButtonGroup
        place_id={params.row.place_id}
        google_places_id={params.row.google_places_id}
        store_url={params.row.data_source_browseable_url}
      />
    ),
  },
  {
    field: "brand_name",
    headerName: "Brand Name",
    type: "string",
    width: 200,
  },
  {
    field: "website",
    headerName: "Website",
    width: 250,
    renderCell: (params: any) => {
      return params.row.website ? (
        <Link
          to={`${params.row.website}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params.value} <RiExternalLinkLine size="14" />
        </Link>
      ) : (
        "--"
      );
    },
  },
  {
    field: "open_status",
    headerName: "Open Status",
    type: "string",
    width: 100,
  },
  {
    field: "location_name",
    headerName: "Location Name",
    type: "string",
    width: 250,
  },
  {
    field: "location_parent_id",
    headerName: "Location Parent ID",
    type: "string",
    width: 250,
  },
  {
    field: "business_category_id",
    headerName: "Business Category ID",
    type: "string",
    width: 250,
  },
  {
    field: "coordinates",
    headerName: "Coordinates",
    type: "string",
    width: 250,
    valueGetter: (value, row) => {
      let coordinates = row.coordinates;
      return coordinates.lng && coordinates.lat
        ? `(${coordinates.lng}, ${coordinates.lat})`
        : "--";
    },
  },
  {
    field: "address",
    headerName: "Address",
    description: "The address of the place.",
    type: "string",
    width: 300,
  },
  {
    field: "city",
    headerName: "City",
    description: "The city of the place.",
    type: "string",
    width: 200,
  },
  {
    field: "state",
    headerName: "State",
    description: "The state of the place.",
    type: "string",
    width: 150,
  },
  {
    field: "postal_code",
    headerName: "Postal Code",
    description: "The postal code of the place.",
    type: "string",
    width: 150,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    type: "string",
    width: 150,
  },
  {
    field: "confidence_score_v2",
    headerName: "Confidence Score V2",
    type: "number",
    width: 200,
  },
  {
    field: "validity_assessment",
    headerName: "Validity Assessment",
    type: "number",
    width: 200,
  },
  {
    field: "data_last_updated",
    headerName: "Data Last Updated",
    description: "The date this data was retrieved from places.",
    type: "string",
    width: 200,
  },
  {
    field: "google_review_count",
    headerName: "Google Review Count",
    type: "number",
    width: 150,
  },
  {
    field: "google_review_count_observation_date",
    headerName: "Google Review Count Observation Date",
    type: "string",
    width: 230,
    sortable: false,
  },
  {
      field: "source_keys",
      headerName: "Sources",
      type: "string",
      width: 200,
      filterable: true,
      filterOperators: getGridStringOperators(),
    
      renderCell: (params: any) => {
        const sourceList = params.row?.source_list;
        if (!sourceList || Object.keys(sourceList).length === 0) return "--";
    
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <span>{Object.keys(sourceList).length}</span>
            <PlaceDetailsModalCell data={sourceList} label="Source List" />
          </Box>
        );
      },
  },
  {
    field: "google_review_count_history",
    headerName: "Review History",
    width: 120,
    renderCell: (params: any) =>
      params.row?.google_review_count_history ? (
        <PlaceDetailsModalCell
          data={params.row.google_review_count_history}
          label="Review Count History"
        />
      ) : (
        "--"
      ),
  },
  {
    field: "last_observations",
    headerName: "Last Observations",
    width: 120,
    renderCell: (params: any) =>
      params.row?.last_observations ? (
        <PlaceDetailsModalCell
          data={params.row.last_observations}
          label="Last Observations"
        />
      ) : (
        "--"
      ),
  }
];

const PlacesDrillDownModal: React.FC<PlacesDrillDownModalProps> = ({
  api,
  apiParams,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Place_Detail_T[]>([]);
  const [filterVisibleOnly, setFilterVisibleOnly] = useState(false);

  const [visiblePlaces, setVisiblePlaces] = useState<Place_Detail_T[]>([]);

  const { apiRequest } = useApi();

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");

  useEffect(() => {
    if (api && apiParams && open) {
      setLoading(true);
      const params = new URLSearchParams(apiParams);

      const fetchData = async () => {
        try {
          const response = await apiRequest<Place_Detail_T[]>(
            `${api}?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            const transformedData = response.data.map((row) => {
              const externalKeys: string[] = [];
              if (row.google_places_id) externalKeys.push("google");
              if (row.place_id) externalKeys.push("kuebiko");
              if (row.store_url) externalKeys.push("store");
              const sourceKeys = row.source_list
              ? Object.keys(row.source_list)
              : [];
  
              return {
                ...row,
                // This is the plain string MUI can filter on
                external_links: externalKeys.join(", "),
                source_keys: sourceKeys.join(", "),
                source_count: sourceKeys.length,
              };
            });
            setData(transformedData);
            // setData(response.data);
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else {
      setData([]);
    }
  }, [api, apiParams, open]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSnackbarClose = () => setSnackbarOpen(false);
  const handleVisiblePlacesChange = (visible: Place_Detail_T[]) => {
    setVisiblePlaces(visible);
  };

  const handleCopyPlaceIds = () => {
    const placeIds =
      filterVisibleOnly == true
        ? visiblePlaces.map((row: any) => row.place_id).filter(Boolean)
        : data.map((row: any) => row.place_id).filter(Boolean);
    if (placeIds.length === 0) {
      setSnackbarMessage("No place IDs available to copy.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    const placeIdsString = placeIds.join(",");
    navigator.clipboard.writeText(placeIdsString).then(() => {
      setSnackbarMessage("Copied places to clipboard!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    });
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        View Places
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "2000px",
            },
          },
        }}
      >
        <DialogTitle>
          Places Drill Down
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <RiCloseLine />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Data Grid Accordion */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<RiArrowDownSLine size={24} />}>
              <Typography variant="h6">Places Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Copy Place IDs Button & Toggle Button */}
              <Box display="flex" justifyContent="flex-end" mb={2} gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => setFilterVisibleOnly((prev) => !prev)}
                >
                  {filterVisibleOnly
                    ? "Show All Locations"
                    : "Show Only Visible Locations"}
                </Button>
                <Button variant="outlined" onClick={handleCopyPlaceIds}>
                  Copy Place IDs
                </Button>
              </Box>
              <GaneshDataGrid
                gridId="placesDrillDownGrid"
                columns={columns_def}
                data={filterVisibleOnly ? visiblePlaces : data} // Switch between filtered and all places
                loading={loading}
                headerFilters={
                  (filterVisibleOnly ? visiblePlaces : data).length > 0
                }
                customHeight="750px"
              />
            </AccordionDetails>
          </Accordion>

          {/* Map Accordion */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<RiArrowDownSLine size={24} />}>
              <Typography variant="h6">Map View</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <LeafletMapPlaces
                locations={data}
                onVisibleChange={handleVisiblePlacesChange}
                onResetZoom={() => setFilterVisibleOnly(false)} // Reset to show all places
              />
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            icon={<RiClipboardLine size={24} />}
          >
            <Box>
              <Typography variant="body1">{snackbarMessage}</Typography>
            </Box>
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
};

export default PlacesDrillDownModal;
